"use client";

import { useEffect, useRef } from "react";

const animationSelector = [
    "[data-builder-animation]",
    '[style*="--builder-animation-name"]',
].join(",");

type AnimationRuntimeState = {
    mode: "appearance" | "scroll";
    trigger: "always" | "once";
};

type SharedAnimationRuntime = {
    release: () => void;
    users: number;
};

const sharedRuntimes = new WeakMap<Document, SharedAnimationRuntime>();

function animationOption(
    element: HTMLElement,
    attribute: string,
    property: string,
): string {
    return element.getAttribute(attribute)
        ?? element.style.getPropertyValue(property).trim();
}

function startAnimationRuntime(ownerDocument: Document): () => void {
    const view = ownerDocument.defaultView;

    if (!view) {
        return () => undefined;
    }

    const HTMLElementConstructor = view.HTMLElement;
    const IntersectionObserverConstructor = view.IntersectionObserver;
    const MutationObserverConstructor = view.MutationObserver;
    const registered = new WeakMap<HTMLElement, AnimationRuntimeState>();
    const intersection = typeof IntersectionObserverConstructor === "undefined"
        ? null
        : new IntersectionObserverConstructor((entries) => {
            for (const entry of entries) {
                const element = entry.target as HTMLElement;
                const trigger = registered.get(element)?.trigger ?? "once";

                if (entry.isIntersecting) {
                    element.setAttribute("data-builder-animation-active", "true");

                    if (trigger === "once") {
                        intersection?.unobserve(element);
                    }
                } else if (trigger === "always") {
                    element.removeAttribute("data-builder-animation-active");
                }
            }
        }, { threshold: 0.12 });
    const unregister = (element: HTMLElement) => {
        intersection?.unobserve(element);
        registered.delete(element);
        element.removeAttribute("data-builder-animation-active");
    };
    const configure = (element: HTMLElement, force = false) => {
        if (!element.matches(animationSelector)) {
            unregister(element);
            return;
        }

        const rawMode = animationOption(
            element,
            "data-builder-animation-mode",
            "--builder-animation-mode",
        );
        const nextState: AnimationRuntimeState = {
            mode: rawMode === "scroll" ? "scroll" : "appearance",
            trigger: animationOption(
                element,
                "data-builder-animation-trigger",
                "--builder-animation-trigger",
            ) === "always"
                ? "always"
                : "once",
        };
        const current = registered.get(element);

        if (
            !force
            && current?.mode === nextState.mode
            && current.trigger === nextState.trigger
        ) {
            return;
        }

        intersection?.unobserve(element);
        registered.set(element, nextState);
        element.setAttribute("data-builder-animation-mode", nextState.mode);
        element.setAttribute("data-builder-animation-trigger", nextState.trigger);

        if (nextState.mode === "scroll") {
            element.setAttribute("data-builder-animation-active", "true");
            return;
        }

        element.removeAttribute("data-builder-animation-active");

        if (intersection) {
            intersection.observe(element);
        } else {
            element.setAttribute("data-builder-animation-active", "true");
        }
    };
    const candidates = (root: ParentNode): HTMLElement[] => {
        const result = root instanceof HTMLElementConstructor
            && root.matches(animationSelector)
                ? [root]
                : [];

        result.push(...Array.from(
            root.querySelectorAll<HTMLElement>(animationSelector),
        ));

        return result;
    };
    const register = (root: ParentNode) => {
        for (const element of candidates(root)) {
            configure(element);
        }
    };
    const unregisterTree = (root: ParentNode) => {
        if (root instanceof HTMLElementConstructor) {
            unregister(root);
        }

        for (const element of root.querySelectorAll<HTMLElement>(animationSelector)) {
            unregister(element);
        }
    };
    const mutations = new MutationObserverConstructor((entries) => {
        for (const entry of entries) {
            if (
                entry.type === "attributes"
                && entry.target instanceof HTMLElementConstructor
            ) {
                configure(
                    entry.target,
                    entry.attributeName === "style"
                    || entry.attributeName === "data-builder-animation",
                );
                continue;
            }

            for (const node of entry.removedNodes) {
                if (node instanceof HTMLElementConstructor) {
                    unregisterTree(node);
                }
            }

            for (const node of entry.addedNodes) {
                if (node instanceof HTMLElementConstructor) {
                    register(node);
                }
            }
        }
    });

    register(ownerDocument);
    mutations.observe(ownerDocument.documentElement, {
        attributeFilter: [
            "data-builder-animation",
            "data-builder-animation-mode",
            "data-builder-animation-trigger",
            "style",
        ],
        attributes: true,
        childList: true,
        subtree: true,
    });

    return () => {
        intersection?.disconnect();
        mutations.disconnect();
    };
}

function retainAnimationRuntime(ownerDocument: Document): () => void {
    let sharedRuntime = sharedRuntimes.get(ownerDocument);

    if (!sharedRuntime) {
        sharedRuntime = {
            release: startAnimationRuntime(ownerDocument),
            users: 0,
        };
        sharedRuntimes.set(ownerDocument, sharedRuntime);
    }

    const runtime = sharedRuntime;

    runtime.users += 1;

    return () => {
        runtime.users -= 1;

        if (
            runtime.users === 0
            && sharedRuntimes.get(ownerDocument) === runtime
        ) {
            runtime.release();
            sharedRuntimes.delete(ownerDocument);
        }
    };
}

export function SiteBuilderInteractionRuntime() {
    const sentinelRef = useRef<HTMLSpanElement>(null);

    useEffect(() => {
        const ownerDocument = sentinelRef.current?.ownerDocument;

        return ownerDocument
            ? retainAnimationRuntime(ownerDocument)
            : undefined;
    }, []);

    return (
        <span
            aria-hidden="true"
            data-site-builder-interaction-runtime="true"
            ref={sentinelRef}
            style={{ display: "none" }}
        />
    );
}
